import { Suspense, lazy } from 'react';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { useSession } from '@/lib/auth';

// Lazy-loaded so the ~60KB devtools chunk never blocks first paint for
// regular users. Gated by role at render time (super_admin only) — see
// RootComponent below.
const TanStackRouterDevtools = lazy(() =>
  import('@tanstack/router-devtools').then((m) => ({ default: m.TanStackRouterDevtools })),
);

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const { user } = useSession();
  // Devtools surface routing internals (route tree, search-param state). Fine
  // for super_admin who needs to debug; hide from authors/editors/etc. so
  // their UI stays clean. Pre-login we render nothing (no user yet).
  const showDevtools = user?.role.slug === 'super_admin';

  return (
    <>
      <Outlet />
      <Toaster />
      {showDevtools && (
        <Suspense fallback={null}>
          <TanStackRouterDevtools position="bottom-right" />
        </Suspense>
      )}
    </>
  );
}
